// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core';
import {
  type Interval,
  type PlanIntervalProps,
  isYearly,
} from '@zapier/design-system-revenue';

import BillingOption from './BillingOption';

type Props = {
  enticements: {
    month: string,
    year: string,
  },
  hasMeteredBilling: boolean,
  intervals: Array<PlanIntervalProps>,
  isCompact?: boolean,
  isDisabled: boolean,
  onSelectBillingInterval: (planName: string, interval: Interval) => void,
  planName: string,
  selectedBillingInterval: string,
};

const billingOptionsCss = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',

  '@media (max-width: 750px)': {
    flexDirection: 'column',
  },
};

const BillingOptions = (props: Props) => {
  const {
    enticements,
    hasMeteredBilling,
    intervals,
    isCompact,
    isDisabled,
    onSelectBillingInterval,
    planName,
    selectedBillingInterval,
  } = props;

  const billingOptions = intervals.map(interval => {
    const intervalName = isYearly(interval.interval)
      ? 'annual'
      : interval.interval;
    const name = `pay ${intervalName}ly`;

    // for the free plan monthly is the the only option, so always select it:
    const isSelected =
      intervals.length === 1 || selectedBillingInterval === interval.interval;

    const price = interval.monthlyAmount || interval.amount;

    return (
      <BillingOption
        enticement={enticements[interval.interval]}
        isCompact={isCompact}
        isDisabled={isDisabled}
        isMetered={hasMeteredBilling}
        isOnly={intervals.length === 1}
        isSelected={isSelected}
        key={name}
        name={name}
        onClick={() => onSelectBillingInterval(planName, interval.interval)}
        price={price}
      />
    );
  });

  return (
    <div
      aria-label="billing interval options"
      css={billingOptionsCss}
      role="radiogroup"
    >
      {billingOptions}
    </div>
  );
};

BillingOptions.defaultProps = {
  isDisabled: false,
};

export default BillingOptions;
